import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import type { ContactDetails, ContactOpportunity } from "@/hooks/useContactDetails";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, PlusCircle, X } from "lucide-react";
import { useAssistants } from "@/hooks/useAssistants";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useTeam } from "@/hooks/useTeam";
import { useTags } from "@/hooks/useTags";
import { usePipelines } from "@/hooks/usePipelines";
import { useContactManagement } from "@/hooks/useContactManagement";
import { useCurrency } from "@/hooks/useCurrency";
import { useLossReasons } from "@/hooks/useLossReasons";
import { deriveOpportunityStatus } from "@/lib/opportunityOutcome";
import {
  dateInputToIso,
  isFutureDateInputValue,
  todayDateInputValue,
} from "@/lib/dateInput";
import { TagSelectorModal } from "@/components/chat/TagSelectorModal";
import { EditOpportunityFullDialog, type OpportunityEditData } from "@/components/crm/EditOpportunityFullDialog";
import { OpportunityCard } from "./OpportunityCard";
import { useTranslation } from "react-i18next";
import {
  useContactFollowUpSequences,
  type ContactFollowUpState,
} from "@/hooks/useContactFollowUpSequences";
import { FOLLOW_UP_STATUS_LABELS, isFollowUpStateLive } from "@/lib/followUpStatus";

type ContactSequenceState = ContactFollowUpState;

interface ContactProps {
  contact?: ContactDetails;
  onUpdate?: (updates: Partial<ContactDetails>) => Promise<boolean>;
  onRefresh?: () => Promise<void>;
}

const Contact: React.FC<ContactProps> = ({ contact, onUpdate, onRefresh }) => {
  const { t } = useTranslation("crm");
  const { user } = useAuth();
  const { assistants } = useAssistants();
  const { members } = useTeam();
  const { tags: availableTags, fetchTags } = useTags();
  const { pipelines, pipelineStages } = usePipelines();
  const contactManagement = useContactManagement();
  const { symbol } = useCurrency();
  const { lossReasons, loading: loadingReasons, createLossReason } = useLossReasons();

  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const [isSequenceModalOpen, setIsSequenceModalOpen] = useState(false);
  const [isOppEditModalOpen, setIsOppEditModalOpen] = useState(false);
  const [isCreateOppModalOpen, setIsCreateOppModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [attributionSearch, setAttributionSearch] = useState("");
  const [contactTags, setContactTags] = useState<string[]>([]);
  const [selectedSequenceId, setSelectedSequenceId] = useState("");
  // Mesmo caminho usado pelo modal do chat: a inclusão passa pela RPC que marca
  // enrollment_source = 'manual', sem a qual o motor nunca dispara a cadência.
  const {
    sequences: contactSequences,
    availableSequences,
    loading: sequencesLoading,
    enroll: enrollInSequence,
    stop: stopSequence,
  } = useContactFollowUpSequences(contact?.id);
  const [newOppForm, setNewOppForm] = useState({
    title: '',
    pipeline_id: '',
    stage_id: '',
    product: '',
    price: '',
  });
  // Nascer numa etapa de ganho/perda é um fechamento: sem estes campos o card
  // entraria na coluna sem nunca ter sido vendido nem perdido, e ficaria fora
  // dos relatórios de venda.
  const [saleDate, setSaleDate] = useState('');
  const [wonByUserId, setWonByUserId] = useState('');
  const [lossReasonId, setLossReasonId] = useState('');
  const [lostByUserId, setLostByUserId] = useState('');
  const [isAddingReason, setIsAddingReason] = useState(false);
  const [newReasonName, setNewReasonName] = useState('');
  const [isCreatingReason, setIsCreatingReason] = useState(false);
  const [oppFormError, setOppFormError] = useState<string | null>(null);

  const [selectedOpportunity, setSelectedOpportunity] = useState<ContactOpportunity | null>(null);

  const activeMembers = members.filter((m) => m.is_active);
  const currentMemberId =
    activeMembers.find((member) => member.auth_user_id === user?.auth_user_id)?.id ?? '';

  const newOppStageType = pipelineStages.find(s => s.id === newOppForm.stage_id)?.stage_type;
  const newOppStatus = deriveOpportunityStatus(newOppStageType);
  const today = todayDateInputValue();
  const saleDateIsFuture = newOppStatus === 'won' && isFutureDateInputValue(saleDate);
  const missingLossReason = newOppStatus === 'lost' && !lossReasonId;

  useEffect(() => {
    setContactTags(contact?.tags || []);
  }, [contact?.tags]);

  // Os padrões (hoje / usuário logado) entram aqui e não no clique da etapa porque
  // a equipe carrega de forma assíncrona: escolher "Ganho" antes disso deixaria o
  // responsável vazio para sempre.
  useEffect(() => {
    if (!isCreateOppModalOpen) return;
    if (newOppStatus === 'won') {
      setSaleDate((current) => current || todayDateInputValue());
      setWonByUserId((current) => current || currentMemberId);
    } else if (newOppStatus === 'lost') {
      setLostByUserId((current) => current || currentMemberId);
    }
  }, [isCreateOppModalOpen, newOppStatus, currentMemberId]);

  if (!contact) return null;

  const handleApplyTags = async (newTags: string[]) => {
    const success = await contactManagement.setTags(contact.id, newTags);
    if (success) {
      setContactTags(newTags);
      onRefresh?.();
    }
  };

  const handleRemoveTag = async (tagToRemove: string) => {
    const success = await contactManagement.removeTag(contact.id, tagToRemove);
    if (success) {
      setContactTags(prev => prev.filter(t => t !== tagToRemove));
      onRefresh?.();
    }
  };

  const handleTagCreated = () => {
    fetchTags();
  };

  const handleAddToSequence = async () => {
    if (!selectedSequenceId) return;

    if (contactSequences.some((item) => item.follow_up_id === selectedSequenceId && item.status === "active")) {
      alert(t("contact.alreadyInSequence"));
      return;
    }

    setIsSaving(true);
    try {
      const result = await enrollInSequence(selectedSequenceId);
      if (!result.ok) throw new Error(result.error);

      setSelectedSequenceId("");
      setIsSequenceModalOpen(false);
      await onRefresh?.();
    } catch (error) {
      console.error("Erro ao adicionar contato à sequência:", error);
      alert(t("contact.addSequenceError"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleStopSequence = async (sequenceState: ContactSequenceState) => {
    const sequenceName = sequenceState.follow_up?.name || "Sequência";
    if (!confirm(t("contact.confirmEndSequence", { name: sequenceName }))) return;

    setIsSaving(true);
    try {
      const result = await stopSequence(sequenceState.id);
      if (!result.ok) throw new Error(result.error);

      await onRefresh?.();
    } catch (error) {
      console.error("Erro ao encerrar sequência:", error);
      alert(t("contact.endSequenceError"));
    } finally {
      setIsSaving(false);
    }
  };

  const resetOutcomeFields = () => {
    setSaleDate('');
    setWonByUserId('');
    setLossReasonId('');
    setLostByUserId('');
    setIsAddingReason(false);
    setNewReasonName('');
  };

  // Trocar de etapa limpa os campos do desfecho anterior, senão um card criado em
  // "Ganho" depois de passar por "Perdido" levaria junto o motivo da perda.
  const handleStageChange = (newStageId: string) => {
    setNewOppForm(prev => ({ ...prev, stage_id: newStageId }));
    setOppFormError(null);
    const stageType = pipelineStages.find(s => s.id === newStageId)?.stage_type ?? 'normal';

    if (stageType === 'won') {
      setLossReasonId('');
      setLostByUserId('');
      setIsAddingReason(false);
    } else if (stageType === 'lost') {
      setSaleDate('');
      setWonByUserId('');
    } else {
      resetOutcomeFields();
    }
  };

  const handleCreateNewReason = async () => {
    if (!newReasonName.trim()) return;
    setIsCreatingReason(true);
    const newReason = await createLossReason(newReasonName.trim());
    setIsCreatingReason(false);
    if (newReason) {
      setLossReasonId(newReason.id);
      setNewReasonName('');
      setIsAddingReason(false);
      setOppFormError(null);
    }
  };

  const closeCreateOppModal = () => {
    setIsCreateOppModalOpen(false);
    setNewOppForm({ title: '', pipeline_id: '', stage_id: '', product: '', price: '' });
    resetOutcomeFields();
    setOppFormError(null);
  };

  const handleCreateOpportunity = async () => {
    if (!user?.account_id || !supabase || !contact) return;
    if (!newOppForm.title.trim() || !newOppForm.pipeline_id || !newOppForm.stage_id) return;

    // Etapa de ganho/perda cria um card já fechado: sem estes dados ele entraria
    // na coluna sem contar como venda (nem como perda) em nenhum relatório.
    if (newOppStatus === 'won' && !saleDate) {
      setOppFormError(t("addOpportunity.saleDateRequired"));
      return;
    }

    if (saleDateIsFuture) {
      setOppFormError(t("editOpportunity.saleDateFuture"));
      return;
    }

    if (missingLossReason) {
      setOppFormError(t("editOpportunity.lossReasonMissing"));
      return;
    }

    setIsSaving(true);
    setOppFormError(null);
    try {
      const priceValue = newOppForm.price ? parseFloat(newOppForm.price.replace(/\D/g, '')) / 100 : 0;
      const derivedStatus = newOppStatus;
      const closedAt =
        derivedStatus === 'won'
          ? dateInputToIso(saleDate) || new Date().toISOString()
          : derivedStatus === 'lost'
            ? new Date().toISOString()
            : null;
      const { error } = await supabase
        .from('account_opportunities')
        .insert({
          account_id: user.account_id,
          contact_id: contact.id,
          pipeline_id: newOppForm.pipeline_id,
          stage_id: newOppForm.stage_id,
          title: newOppForm.title.trim(),
          product: newOppForm.product.trim() || null,
          price: priceValue,
          status: derivedStatus,
          // Nascer direto numa etapa de ganho/perda já é um fechamento
          closed_at: closedAt,
          sale_date: derivedStatus === 'won' ? dateInputToIso(saleDate) ?? null : null,
          won_by_user_id: derivedStatus === 'won' ? wonByUserId || null : null,
          loss_reason_id: derivedStatus === 'lost' ? lossReasonId || null : null,
          lost_by_user_id: derivedStatus === 'lost' ? lostByUserId || null : null,
          tags: [],
          order_index: 0,
        });

      if (error) throw error;

      closeCreateOppModal();
      onRefresh?.();
    } catch (error) {
      console.error("Error creating opportunity:", error);
      setOppFormError(error instanceof Error ? error.message : String(error));
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditOpportunity = (opp: ContactOpportunity) => {
    setSelectedOpportunity(opp);
    setIsOppEditModalOpen(true);
  };

  const handleAddOpportunityClick = () => {
    setIsCreateOppModalOpen(true);
  };

  // Cards que o contato já tem no funil escolhido no formulário. Não bloqueia a
  // criação — o mesmo lead pode percorrer o mesmo funil mais de uma vez.
  const duplicatesInSelectedPipeline = newOppForm.pipeline_id
    ? (contact.opportunities || []).filter(opp => opp.pipeline_id === newOppForm.pipeline_id)
    : [];

  // O diálogo de edição já coleta motivo da perda, data da venda e responsável, então
  // a gravação acontece de uma vez só — sem um segundo pop-up repetindo os campos
  const handleSaveOpportunityFull = async (data: OpportunityEditData) => {
    if (!user?.account_id || !selectedOpportunity || !supabase) return;

    const updateData: Record<string, unknown> = {
      title: data.title,
      pipeline_id: data.pipeline_id,
      stage_id: data.stage_id,
      product: data.product || null,
      price: data.price || 0,
      status: data.status,
      notes: data.notes || null,
      updated_at: new Date().toISOString(),
    };

    // Cada desfecho limpa os campos do outro, senão um card reaberto e fechado do
    // lado oposto fica com dados contraditórios
    if (data.status === 'won') {
      updateData.closed_at = data.closed_at || new Date().toISOString();
      updateData.sale_date = data.sale_date || null;
      updateData.won_by_user_id = data.won_by_user_id || null;
      updateData.loss_reason_id = null;
      updateData.lost_by_user_id = null;
    } else if (data.status === 'lost') {
      updateData.closed_at = data.closed_at || new Date().toISOString();
      updateData.loss_reason_id = data.loss_reason_id || null;
      updateData.lost_by_user_id = data.lost_by_user_id || null;
      updateData.sale_date = null;
      updateData.won_by_user_id = null;
    } else {
      updateData.closed_at = null;
      updateData.sale_date = null;
      updateData.won_by_user_id = null;
      updateData.lost_by_user_id = null;
      updateData.loss_reason_id = null;
    }

    const { error } = await supabase
      .from('account_opportunities')
      .update(updateData)
      .eq('id', selectedOpportunity.id)
      .eq('account_id', user.account_id);

    if (error) throw error;

    setIsOppEditModalOpen(false);
    setSelectedOpportunity(null);
    onRefresh?.();
  };

  const handleAssistantToggle = async (checked: boolean) => {
    if (!onUpdate) return;
    await onUpdate({ assistant_is_active: checked });
    onRefresh?.();
  };

  const handleAssistantChange = async (assistantId: string) => {
    if (!onUpdate) return;
    await onUpdate({ assistant_id: assistantId === 'none' ? null : assistantId });
    onRefresh?.();
  };

  const handleAttributionChange = async (attributionId: string) => {
    if (!onUpdate) return;
    await onUpdate({ attribution_id: attributionId === 'none' ? null : attributionId });
    onRefresh?.();
  };

  const sortedOpportunities = [...(contact.opportunities || [])].sort((a, b) => {
    const statusOrder = { open: 0, won: 1, lost: 2 };
    const statusDiff = statusOrder[a.status] - statusOrder[b.status];
    if (statusDiff !== 0) return statusDiff;
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  const panelCard =
    "w-full p-4 gap-3 flex rounded-xl flex-col bg-white border border-[#E5E7EB] shadow-sm transition-colors duration-200 dark:bg-[#27272A] dark:border-[#3F3F46] dark:shadow-none";
  const panelTitle = "text-base font-semibold text-[#0F172A] dark:text-[#FAFAFA]";
  const selectTrigger =
    "w-full rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] transition-colors duration-200 focus:border-[#2563EB] dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA] dark:focus:border-[#60A5FA]";
  const emptyText = "text-sm text-[#94A3B8] dark:text-[#71717A]";
  const dialogShell =
    "rounded-2xl border border-[#E5E7EB] bg-white text-[#0F172A] dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA]";
  const dialogInput =
    "h-11 rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] transition-colors duration-200 dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A] dark:focus-visible:border-[#60A5FA]";
  const primaryBtn = "bg-[#2563EB] hover:bg-[#1D4ED8] text-white transition-colors duration-200";
  const outlineBtn =
    "rounded-xl border border-[#E5E7EB] bg-white text-[#334155] hover:bg-[#F1F5F9] transition-colors duration-200 dark:border-[#3F3F46] dark:bg-transparent dark:text-[#D4D4D8] dark:hover:bg-[#3F3F46]";
  const tagClass =
    "capitalize flex items-center gap-1 pr-1 group cursor-pointer bg-[#DBEAFE] text-[#2563EB] border-0 hover:bg-[#BFDBFE] transition-colors duration-200 dark:bg-blue-500/20 dark:text-[#60A5FA] dark:hover:bg-blue-500/30";

  return (
    <div className="w-full flex flex-col gap-3 xl:order-3 order-2">
      {/* Assistant Section */}
      <div className={panelCard}>
        <div className="w-full flex justify-between items-center gap-2">
          <h3 className={panelTitle}>
            {t("contact.assistantOn")}
          </h3>
          <Switch
            checked={contact.assistant_is_active}
            onCheckedChange={handleAssistantToggle}
          />
        </div>
        <Select
          value={contact.assistant_id || 'none'}
          onValueChange={handleAssistantChange}
        >
          <SelectTrigger className={selectTrigger}>
            <SelectValue placeholder={t("contact.pickAssistant")} />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem value="none">{t("contact.noAssistant")}</SelectItem>
              {assistants.filter(a => a.is_active).map((assistant) => (
                <SelectItem key={assistant.id} value={assistant.id}>
                  {assistant.name}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>

      {/* Attribution Section */}
      <div className={panelCard}>
        <h3 className={panelTitle}>{t("contact.owner")}</h3>
        <Select
          value={contact.attribution_id || 'none'}
          onValueChange={handleAttributionChange}
          onOpenChange={(open) => {
            if (!open) setAttributionSearch('');
          }}
        >
          <SelectTrigger className={selectTrigger}>
            <SelectValue placeholder={t("contact.pickOwner")} />
          </SelectTrigger>
          <SelectContent>
            <div className="px-2 pb-2">
              <Input
                placeholder={t("contact.searchOwner")}
                value={attributionSearch}
                onChange={(e) => setAttributionSearch(e.target.value)}
                className="h-8 rounded-lg border border-[#E5E7EB] bg-white text-[#0F172A] placeholder:text-[#94A3B8] dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            <SelectGroup>
              <SelectItem value="none">{t("contact.noOwner")}</SelectItem>
              {members
                .filter(m => m.is_active && m.auth_user_id)
                .filter(m => {
                  if (!attributionSearch) return true;
                  const searchLower = attributionSearch.toLowerCase();
                  const name = (m.name || '').toLowerCase();
                  const email = (m.email || '').toLowerCase();
                  return name.includes(searchLower) || email.includes(searchLower);
                })
                .map((member) => (
                  <SelectItem key={member.auth_user_id} value={member.auth_user_id!}>
                    {member.name || member.email}
                  </SelectItem>
                ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>

      {/* Opportunities Section */}
      <div className={panelCard}>
        <div className="w-full flex justify-between items-center">
          <h3 className={panelTitle}>{t("contact.opportunities")}</h3>
          <button
            onClick={handleAddOpportunityClick}
            className="text-[#64748B] hover:text-[#0F172A] transition-colors duration-200 dark:text-[#A1A1AA] dark:hover:text-[#FAFAFA]"
            aria-label={t("contact.addOpportunity")}
          >
            <PlusCircle className="w-6 h-6" />
          </button>
        </div>
        <div className="flex flex-col gap-3 w-full">
          {sortedOpportunities.length > 0 ? (
            sortedOpportunities.map((opp) => (
              <OpportunityCard
                key={opp.id}
                opportunity={opp}
                onEdit={handleEditOpportunity}
              />
            ))
          ) : (
            <p className={emptyText}>{t("contact.noOpportunities")}</p>
          )}
        </div>
      </div>

      {/* Tags Section */}
      <div className={panelCard}>
        <h3 className={panelTitle}>{t("contact.tags")}</h3>
        <div className="flex flex-wrap gap-2 w-full">
          <Badge
            variant="default2"
            className="bg-[#F1F5F9] text-[#334155] hover:bg-[#EEF4FF] hover:text-[#2563EB] flex items-center cursor-pointer px-2 py-1 border border-[#E5E7EB] transition-colors duration-200 dark:bg-[#3F3F46] dark:text-[#D4D4D8] dark:border-[#3F3F46] dark:hover:bg-[rgba(37,99,235,.18)] dark:hover:text-[#60A5FA]"
            onClick={() => setIsTagModalOpen(true)}
          >
            <Plus className="w-4 h-4 mr-1" />
            <span>{t("contact.add")}</span>
          </Badge>

          {contactTags.map((tag) => (
            <Badge
              key={tag}
              className={tagClass}
            >
              <span>{tag}</span>
              <button
                onClick={() => handleRemoveTag(tag)}
                className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity"
                disabled={contactManagement.updating}
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))}

          {contactTags.length === 0 && (
            <p className="text-xs text-[#94A3B8] dark:text-[#71717A]">{t("contact.noTags")}</p>
          )}
        </div>
      </div>

      {/* Campaigns Section */}
      <div className={panelCard}>
        <div className="w-full flex justify-between items-center">
          <h3 className={panelTitle}>{t("contact.campaigns")}</h3>
        </div>
        <div className="flex flex-wrap gap-2 w-full">
          {contact.campaigns && contact.campaigns.length > 0 ? (
            contact.campaigns.map((campaign, index) => (
              <Badge
                key={index}
                className="py-1 px-3 bg-[#DBEAFE] text-[#2563EB] border-0 transition-colors duration-200 dark:bg-blue-500/20 dark:text-[#60A5FA]"
              >
                {campaign}
              </Badge>
            ))
          ) : (
            <p className={emptyText}>{t("contact.noCampaigns")}</p>
          )}
        </div>
      </div>

      {/* Sequences Section */}
      <div className={panelCard}>
        <div className="w-full flex justify-between items-center">
          <h3 className={panelTitle}>{t("contact.sequences")}</h3>
          <button
            onClick={() => {
              setSelectedSequenceId("");
              setIsSequenceModalOpen(true);
            }}
            className="text-[#64748B] hover:text-[#0F172A] transition-colors duration-200 dark:text-[#A1A1AA] dark:hover:text-[#FAFAFA]"
            aria-label={t("contact.addToSequence")}
          >
            <PlusCircle className="w-6 h-6" />
          </button>
        </div>
        <div className="flex flex-col gap-2 w-full">
          {sequencesLoading ? (
            <p className={emptyText}>{t("contact.loadingSequences")}</p>
          ) : contactSequences.length > 0 ? (
            contactSequences.map((sequenceState) => {
              const statusMeta =
                FOLLOW_UP_STATUS_LABELS[sequenceState.status] || FOLLOW_UP_STATUS_LABELS.stopped;
              const canStop = isFollowUpStateLive(sequenceState.status);
              return (
                <div
                  key={sequenceState.id}
                  className="flex items-center justify-between gap-2 rounded-lg border border-[#E5E7EB] bg-[#F8FAFC] px-3 py-2 dark:border-[#3F3F46] dark:bg-[#18181B]"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-[#0F172A] dark:text-[#FAFAFA]">
                      {sequenceState.follow_up?.name || t("detail.sequenceFallback")}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusMeta.className}`}
                    >
                      {t(statusMeta.labelKey)}
                    </span>
                    {canStop && (
                      <button
                        type="button"
                        onClick={() => void handleStopSequence(sequenceState)}
                        disabled={isSaving}
                        className="rounded-md p-1 text-[#64748B] transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-50 dark:text-[#A1A1AA] dark:hover:bg-red-500/15 dark:hover:text-red-400"
                        aria-label={`Encerrar sequência ${sequenceState.follow_up?.name || ""}`}
                        title={t("contact.endSequence")}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <p className={emptyText}>{t("contact.noSequences")}</p>
          )}
        </div>
      </div>

      <TagSelectorModal
        open={isTagModalOpen}
        onOpenChange={setIsTagModalOpen}
        availableTags={availableTags}
        selectedTags={contactTags}
        onApplyTags={handleApplyTags}
        onTagCreated={handleTagCreated}
      />

      {/* Sequence Modal */}
      <Dialog
        open={isSequenceModalOpen}
        onOpenChange={(open) => {
          setIsSequenceModalOpen(open);
          if (!open) setSelectedSequenceId("");
        }}
      >
        <DialogContent className={dialogShell}>
          <DialogHeader>
            <DialogTitle className="text-[#0F172A] dark:text-[#FAFAFA]">
              {t("contact.addToSequenceTitle")}
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="sequence-select" className="text-[#0F172A] dark:text-[#FAFAFA]">
                {t("contact.sequence")}
              </Label>
              <Select value={selectedSequenceId} onValueChange={setSelectedSequenceId}>
                <SelectTrigger id="sequence-select" className={selectTrigger}>
                  <SelectValue placeholder={t("contact.pickSequence")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {availableSequences.length > 0 ? (
                      availableSequences.map((sequence) => (
                        <SelectItem key={sequence.id} value={sequence.id}>
                          {sequence.name}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="__none__" disabled>
                        {t("contact.noActiveSequence")}
                      </SelectItem>
                    )}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setIsSequenceModalOpen(false)}
                className={outlineBtn}
              >
                {t("contact.cancel")}
              </Button>
              <Button
                onClick={handleAddToSequence}
                disabled={!selectedSequenceId || isSaving || availableSequences.length === 0}
                className={primaryBtn}
              >
                {isSaving ? t("detail.addingSequence") : t("detail.addSequence")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Opportunity Modal */}
      <Dialog open={isCreateOppModalOpen} onOpenChange={(open) => { if (!open) closeCreateOppModal(); else setIsCreateOppModalOpen(true); }}>
        <DialogContent className={`${dialogShell} max-w-lg max-h-[90vh] overflow-y-auto`}>
          <DialogHeader>
            <DialogTitle className="text-[#0F172A] dark:text-[#FAFAFA]">{t("contact.newOpportunity")}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label className="text-[#0F172A] dark:text-[#FAFAFA]">{t("contact.titleRequired")}</Label>
              <Input
                value={newOppForm.title}
                onChange={(e) => setNewOppForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder={t("contact.titlePlaceholder")}
                className={dialogInput}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-2">
                <Label className="text-[#0F172A] dark:text-[#FAFAFA]">{t("contact.pipelineRequired")}</Label>
                <Select
                  value={newOppForm.pipeline_id}
                  onValueChange={(value) => {
                    setNewOppForm(prev => ({ ...prev, pipeline_id: value, stage_id: '' }));
                    resetOutcomeFields();
                    setOppFormError(null);
                  }}
                >
                  <SelectTrigger className={selectTrigger}>
                    <SelectValue placeholder={t("contact.pick")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {pipelines.map((pipeline) => (
                        <SelectItem key={pipeline.id} value={pipeline.id}>
                          {pipeline.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-2">
                <Label className="text-[#0F172A] dark:text-[#FAFAFA]">{t("contact.stageRequired")}</Label>
                <Select
                  value={newOppForm.stage_id}
                  onValueChange={handleStageChange}
                  disabled={!newOppForm.pipeline_id}
                >
                  <SelectTrigger className={selectTrigger}>
                    <SelectValue placeholder={t("contact.pick")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {pipelineStages
                        .filter(s => s.pipeline_id === newOppForm.pipeline_id)
                        .map((stage) => (
                          <SelectItem key={stage.id} value={stage.id}>
                            {stage.name}
                          </SelectItem>
                        ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {newOppStatus === 'won' && (
              <>
                <div className="flex flex-col gap-2">
                  <Label className="text-[#0F172A] dark:text-[#FAFAFA]">
                    {t("editOpportunity.saleDate")} *
                  </Label>
                  <Input
                    type="date"
                    value={saleDate}
                    max={today}
                    onChange={(e) => { setSaleDate(e.target.value); setOppFormError(null); }}
                    aria-invalid={saleDateIsFuture}
                    className={`${dialogInput} [color-scheme:light] dark:[color-scheme:dark]`}
                  />
                  {saleDateIsFuture && (
                    <p className="text-xs text-red-500 dark:text-red-400">
                      {t("editOpportunity.saleDateFuture")}
                    </p>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  <Label className="text-[#0F172A] dark:text-[#FAFAFA]">{t("editOpportunity.seller")}</Label>
                  <Select
                    value={wonByUserId || 'none'}
                    onValueChange={(v) => setWonByUserId(v === 'none' ? '' : v)}
                  >
                    <SelectTrigger className={selectTrigger}>
                      <SelectValue placeholder={t("contact.pick")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem value="none">{t("common.none")}</SelectItem>
                        {activeMembers.map((member) => (
                          <SelectItem key={member.id} value={member.id}>
                            {member.name || member.email}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {newOppStatus === 'lost' && (
              <>
                <div className="flex flex-col gap-2">
                  <Label className="text-[#0F172A] dark:text-[#FAFAFA]">
                    {t("editOpportunity.lossReasonRequired")}
                  </Label>
                  <Select
                    value={lossReasonId}
                    onValueChange={(value) => {
                      if (value === '__new__') {
                        setIsAddingReason(true);
                        setLossReasonId('');
                      } else {
                        setLossReasonId(value);
                        setIsAddingReason(false);
                        setOppFormError(null);
                      }
                    }}
                    disabled={loadingReasons}
                  >
                    <SelectTrigger className={selectTrigger} aria-invalid={missingLossReason}>
                      <SelectValue placeholder={loadingReasons ? t("common:shared.loading") : t("loss.pickReason")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        {lossReasons.map((reason) => (
                          <SelectItem key={reason.id} value={reason.id}>
                            {reason.name}
                          </SelectItem>
                        ))}
                        <SelectItem value="__new__">
                          <span className="flex items-center gap-2 text-[#2563EB] dark:text-[#60A5FA]">
                            <Plus className="w-4 h-4" />
                            {t("loss.addReason")}
                          </span>
                        </SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>

                {isAddingReason && (
                  <div className="flex flex-col gap-2 rounded-xl border border-[#E5E7EB] bg-[#F8FAFC] p-3 dark:border-[#3F3F46] dark:bg-[#18181B]">
                    <Label className="text-[#0F172A] dark:text-[#FAFAFA]">{t("loss.newReason")}</Label>
                    <div className="flex gap-2">
                      <Input
                        value={newReasonName}
                        onChange={(e) => setNewReasonName(e.target.value)}
                        placeholder={t("loss.newReasonPlaceholder")}
                        className={`${dialogInput} flex-1`}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            void handleCreateNewReason();
                          }
                        }}
                      />
                      <Button
                        type="button"
                        onClick={() => void handleCreateNewReason()}
                        disabled={!newReasonName.trim() || isCreatingReason}
                        className={primaryBtn}
                      >
                        {isCreatingReason ? '...' : t("common:actions.save")}
                      </Button>
                    </div>
                  </div>
                )}

                <div className="flex flex-col gap-2">
                  <Label className="text-[#0F172A] dark:text-[#FAFAFA]">{t("editOpportunity.lossOwner")}</Label>
                  <Select
                    value={lostByUserId || 'none'}
                    onValueChange={(v) => setLostByUserId(v === 'none' ? '' : v)}
                  >
                    <SelectTrigger className={selectTrigger}>
                      <SelectValue placeholder={t("contact.pick")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem value="none">{t("common.none")}</SelectItem>
                        {activeMembers.map((member) => (
                          <SelectItem key={member.id} value={member.id}>
                            {member.name || member.email}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            <div className="flex flex-col gap-2">
              <Label className="text-[#0F172A] dark:text-[#FAFAFA]">{t("contact.product")}</Label>
              <Input
                value={newOppForm.product}
                onChange={(e) => setNewOppForm(prev => ({ ...prev, product: e.target.value }))}
                placeholder={t("contact.productPlaceholder")}
                className={dialogInput}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label className="text-[#0F172A] dark:text-[#FAFAFA]">Valor ({symbol})</Label>
              <Input
                value={newOppForm.price}
                onChange={(e) => {
                  const rawValue = e.target.value.replace(/\D/g, '');
                  if (!rawValue) { setNewOppForm(prev => ({ ...prev, price: '' })); return; }
                  const num = parseInt(rawValue, 10) / 100;
                  setNewOppForm(prev => ({ ...prev, price: num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }));
                }}
                placeholder="0,00"
                className={dialogInput}
              />
            </div>
            {duplicatesInSelectedPipeline.length > 0 && (
              <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-700 dark:text-amber-300">
                <p className="font-semibold mb-1">{t("duplicateWarning.title")}</p>
                {t("duplicateWarning.body", {
                  count: duplicatesInSelectedPipeline.length,
                  titles: duplicatesInSelectedPipeline.map(opp => opp.title).join(", "),
                })}
              </div>
            )}
            {oppFormError && (
              <div className="rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-600 dark:text-red-400">
                {oppFormError}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={closeCreateOppModal}
                className={outlineBtn}
              >
                {t("contact.cancel")}
              </Button>
              <Button
                onClick={handleCreateOpportunity}
                disabled={!newOppForm.title.trim() || !newOppForm.pipeline_id || !newOppForm.stage_id || isSaving}
                className={primaryBtn}
              >
                {isSaving
                  ? "Criando..."
                  : duplicatesInSelectedPipeline.length > 0
                    ? t("duplicateWarning.confirm")
                    : t("detail.createOpportunity")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Opportunity Edit Modal */}
      <EditOpportunityFullDialog
        open={isOppEditModalOpen}
        onClose={() => {
          setIsOppEditModalOpen(false);
          setSelectedOpportunity(null);
        }}
        onSave={handleSaveOpportunityFull}
        opportunity={selectedOpportunity}
      />
    </div>
  );
};

export default Contact;
